import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { DatePickerField } from "@/components/ui/date-picker-field";
import { Download, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { format, subDays } from "date-fns";
import { useAuditLog } from "@/hooks/useAuditLog";

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}

export default function ReferralExportModal({ open, onOpenChange }: Props) {
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [fmt, setFmt] = useState<"csv" | "xlsx">("csv");
  const [exporting, setExporting] = useState(false);
  const { logAction } = useAuditLog();

  function setPreset(key: string) {
    const today = new Date();
    if (key === "7") { setStartDate(subDays(today, 7)); setEndDate(today); }
    else if (key === "30") { setStartDate(subDays(today, 30)); setEndDate(today); }
    else { setStartDate(undefined); setEndDate(undefined); }
  }

  async function handleExport() {
    setExporting(true);
    // The project's PostgREST max_rows is 1000 — a single request silently
    // truncates past that regardless of any .range() requested. Page through
    // in 1000-row batches so exports aren't capped for larger agencies.
    const PAGE_SIZE = 1000;
    const EXPORT_CAP = 50_000;
    const data: any[] = [];
    let error: any = null;
    for (let offset = 0; offset < EXPORT_CAP; offset += PAGE_SIZE) {
      let query = supabase.from("referral_leads").select("*").order("created_at", { ascending: false });
      if (startDate) query = query.gte("created_at", startDate.toISOString());
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        query = query.lte("created_at", end.toISOString());
      }
      const { data: page, error: pageError } = await query.range(offset, offset + PAGE_SIZE - 1);
      if (pageError) { error = pageError; break; }
      data.push(...(page ?? []));
      if (!page || page.length < PAGE_SIZE) break;
    }
    setExporting(false);
    if (error || !data.length) { toast.error(error?.message || "No data to export"); return; }

    const rows = data.map((r: any) => ({
      Organization: r.org_name,
      Contact: r.contact_name || "",
      Title: r.title || "",
      Phone: r.phone || "",
      ZIP: r.zip_code || "",
      "Source Type": r.source_type || "",
      Status: r.status,
      "Email Status": r.email_status,
      Notes: r.notes || "",
      Created: r.created_at ? format(new Date(r.created_at), "yyyy-MM-dd") : "",
    }));

    if (fmt === "csv") {
      const headers = Object.keys(rows[0]);
      const csv = [headers.join(","), ...rows.map(r => headers.map(h => `"${(r as any)[h] || ""}"`).join(","))].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `referrals_${format(new Date(), "yyyyMMdd")}.csv`; a.click();
      URL.revokeObjectURL(url);
    } else {
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Referrals");
      XLSX.writeFile(wb, `referrals_${format(new Date(), "yyyyMMdd")}.xlsx`);
    }
    logAction("export", "referral_lead", undefined, { count: rows.length, format: fmt });
    toast.success(`Exported ${rows.length} referrals`);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Export Referrals</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setPreset("7")}>Last 7 Days</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset("30")}>Last 30 Days</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset("all")}>All Time</Button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Start Date</Label>
              <DatePickerField value={startDate} onChange={setStartDate} placeholder="Any" disabled={d => !!endDate && d > endDate} />
            </div>
            <div className="space-y-1.5">
              <Label>End Date</Label>
              <DatePickerField value={endDate} onChange={setEndDate} placeholder="Any" disabled={d => !!startDate && d < startDate} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Format</Label>
            <div className="flex gap-2">
              <Button size="sm" variant={fmt === "csv" ? "default" : "outline"} onClick={() => setFmt("csv")}>CSV</Button>
              <Button size="sm" variant={fmt === "xlsx" ? "default" : "outline"} onClick={() => setFmt("xlsx")}>Excel (.xlsx)</Button>
            </div>
          </div>
          <Button className="w-full" onClick={handleExport} disabled={exporting}>
            {exporting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Exporting…</> : <><Download className="h-4 w-4 mr-2" /> Export</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
